import store from '@/store'

/**
 * Check if user has any of the specified roles
 * @param {Array} requiredRoles - Array of role names required
 * @returns {boolean} - Whether user has at least one of the required roles
 */
export function hasAnyRole(requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) {
    return false // If roles are specified, user must have one of them
  }

  const user = store.getters['auth/currentUser']
  if (!user) {
    return false
  }

  // Super admin can access everything
  if (user.is_superadmin) {
    return true
  }

  let userRoleCodes = []

  // Check single role (role_id relationship) - use code for permission checking
  if (user.role && user.role.code) {
    userRoleCodes.push(user.role.code.toLowerCase())
  }

  // Check roles array (legacy many-to-many relationship) - use code for permission checking
  if (user.roles && Array.isArray(user.roles)) {
    user.roles.forEach(role => {
      if (role.code) {
        userRoleCodes.push(role.code.toLowerCase())
      }
    })
  }

  // Check if user has any of the required roles (by code)
  return requiredRoles.some(role => 
    userRoleCodes.includes(role.toLowerCase())
  )
}

/**
 * Check if user has all specified roles
 * @param {Array} requiredRoles - Array of role names required
 * @returns {boolean} - Whether user has all required roles
 */
export function hasAllRoles(requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true
  }

  const user = store.getters['auth/currentUser']
  if (!user || !user.roles) {
    return false
  }

  const userRoleNames = user.roles.map(role => role.code)
  return requiredRoles.every(role => userRoleNames.includes(role))
}

/**
 * Check if user can access a route (role-based access control)
 * @param {Object} route - Vue route object
 * @returns {boolean} - Whether user can access the route
 */
export function canAccessRoute(route) {
  const user = store.getters['auth/currentUser']

  // Super admin can access everything
  if (user && user.is_superadmin) {
    return true
  }

  // Handle route object or resolved route
  const routeMeta = route.meta || (route.matched && route.matched.length > 0 && route.matched[0].meta)

  if (!routeMeta) {
    // If no route meta, hide the link (no roles specified)
    return false
  }

  // If route requires auth and user is not logged in, deny access
  if (routeMeta.requiresAuth && !user) {
    return false
  }

  // If route specifies roles, check if user has required roles
  if (routeMeta.roles && routeMeta.roles.length > 0) {
    return hasAnyRole(routeMeta.roles)
  }

  // If route is public or doesn't require auth, allow access
  if (routeMeta.public === true || routeMeta.requiresAuth === false) {
    return true
  }

  // If no roles are specified and not a public route, hide the link
  return false
}

/**
 * Get user's role codes
 * @returns {Array} - Array of role codes
 */
export function getUserRoles() {
  const user = store.getters['auth/currentUser']
  if (!user || !user.role) {
    return []
  }
  return [user.role.code]
}

/**
 * Check if user is a site representative
 * @returns {boolean}
 */
export function isSiteRepresentative() {
  return hasAnyRole(['site_representative'])
}

/**
 * Check if user is a sponsor
 * @returns {boolean}
 */
export function isSponsor() {
  return hasAnyRole(['sponsor'])
}

/**
 * Legacy permission functions (disabled - always return true for authenticated users)
 */
export function hasPermission() {
  const user = store.getters['auth/currentUser']
  return !!user // Just check if user exists
}

export function hasAnyPermission() {
  const user = store.getters['auth/currentUser']
  return !!user // Just check if user exists
}

export function hasAllPermissions() {
  const user = store.getters['auth/currentUser']
  return !!user // Just check if user exists
}

/**
 * Generate a list of routes the user has access to (permissions disabled)
 * @param {Array} routes - Array of route objects to check
 * @returns {Array} - All routes (permissions disabled)
 */
export function getAccessibleRoutes(routes) {
  return routes.filter(route => {
    if (route.children && route.children.length) {
      const accessibleChildren = getAccessibleRoutes(route.children)
      if (accessibleChildren.length) {
        return {
          ...route,
          children: accessibleChildren
        }
      }
      return false
    }
    return canAccessRoute(route)
  })
}
